import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { usePanelAccess } from './PanelAccessContext';
import {
  THEME_STORAGE_KEY,
  LANG_STORAGE_KEY,
  readStored,
  writeStored,
  applyTheme,
  applyLanguage,
} from './adminTheme';
import {
  listVerifiedTotpFactors,
  enrollTotpFactor,
  verifyTotpCode,
  unenrollFactor,
  mfaErrorMessage,
} from './mfa';
import './SettingsPage.css';

function IconTile({ children }) {
  return (
    <span className="settings-icon-tile" aria-hidden>
      {children}
    </span>
  );
}

function SettingsRow({ label, hint, children, stack = false }) {
  return (
    <div className={`settings-row${stack ? ' settings-row--stack' : ''}`}>
      <div className="settings-row-text">
        <label>{label}</label>
        {hint ? <span>{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const panelAccess = usePanelAccess();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [theme, setTheme] = useState(() => readStored(THEME_STORAGE_KEY, 'light'));
  const [lang, setLang] = useState(() => readStored(LANG_STORAGE_KEY, 'tr'));

  const [mfaFactors, setMfaFactors] = useState([]);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaOk, setMfaOk] = useState('');
  const [enrollDraft, setEnrollDraft] = useState(null); // { id, qr, secret }
  const [enrollCode, setEnrollCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const refreshMfa = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      const factors = await listVerifiedTotpFactors();
      setMfaFactors(factors);
    } catch (err) {
      setMfaError(mfaErrorMessage(err));
      setMfaFactors([]);
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!active) return;
      setUser(data?.user ?? null);
      setLoading(false);
      await refreshMfa();
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    writeStored(THEME_STORAGE_KEY, theme);
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    writeStored(LANG_STORAGE_KEY, lang);
    applyLanguage(lang);
  }, [lang]);

  const handleSignOut = async () => {
    if (!window.confirm('Bu cihazdan çıkış yapılsın mı?')) return;
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  const handleStartEnroll = async () => {
    setMfaBusy(true);
    setMfaError('');
    setMfaOk('');
    setShowDisable(false);
    try {
      // Drop any leftover unverified factors from a previous attempt.
      const { data: listed } = await supabase.auth.mfa.listFactors();
      const pending = (listed?.all || []).filter((f) => f?.factor_type === 'totp' && f?.status === 'unverified');
      await Promise.all(pending.map((f) => unenrollFactor(f.id).catch(() => null)));

      const enrolled = await enrollTotpFactor('MMJ Panel');
      setEnrollDraft({
        id: enrolled.id,
        qr: enrolled.totp?.qr_code || '',
        secret: enrolled.totp?.secret || '',
      });
      setEnrollCode('');
    } catch (err) {
      setMfaError(mfaErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  };

  const handleCancelEnroll = async () => {
    if (!enrollDraft?.id) {
      setEnrollDraft(null);
      return;
    }
    setMfaBusy(true);
    setMfaError('');
    try {
      await unenrollFactor(enrollDraft.id);
    } catch {
      /* ignore */
    } finally {
      setEnrollDraft(null);
      setEnrollCode('');
      setMfaBusy(false);
    }
  };

  const handleConfirmEnroll = async (e) => {
    e.preventDefault();
    if (!enrollDraft?.id) return;
    setMfaBusy(true);
    setMfaError('');
    setMfaOk('');
    try {
      await verifyTotpCode(enrollDraft.id, enrollCode);
      setEnrollDraft(null);
      setEnrollCode('');
      setMfaOk('İki adımlı doğrulama etkin. Sonraki girişlerde kod istenecek.');
      await refreshMfa();
    } catch (err) {
      setMfaError(mfaErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    const factor = mfaFactors[0];
    if (!factor?.id) return;
    setMfaBusy(true);
    setMfaError('');
    setMfaOk('');
    try {
      await verifyTotpCode(factor.id, disableCode);
      await unenrollFactor(factor.id);
      setShowDisable(false);
      setDisableCode('');
      setMfaOk('İki adımlı doğrulama kapatıldı.');
      await refreshMfa();
    } catch (err) {
      setMfaError(mfaErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  };

  const mfaEnabled = mfaFactors.length > 0;
  const initial = (user?.email || '—').charAt(0).toUpperCase();
  const displayName =
    user?.user_metadata?.full_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0]
    || 'Hesap';

  const roleLabel = !panelAccess
    ? '…'
    : panelAccess.isOwner
      ? 'İşletme sahibi'
      : 'Panel yetkilisi';

  return (
    <div className="admin-page-content settings-page">
      <header className="settings-page-hero">
        <div>
          <p className="settings-page-kicker">Hesap</p>
          <h1 className="settings-page-title">Ayarlar</h1>
          <p className="settings-page-sub">
            Hesap bilgisi, güvenlik, görünüm ve oturum.
          </p>
        </div>
      </header>

      <div className="settings-layout settings-layout--simple">
        <div className="settings-col">
          <section className="settings-card settings-card--profile">
            <div className="settings-profile">
              <div className="settings-avatar" aria-hidden>{loading ? '…' : initial}</div>
              <div className="settings-profile-copy">
                <h2>{loading ? 'Yükleniyor…' : displayName}</h2>
                <p>{loading ? ' ' : user?.email || '—'}</p>
                <div className="settings-pill-row">
                  <span className="settings-pill settings-pill--accent">{roleLabel}</span>
                </div>
              </div>
            </div>

            <div className="settings-list">
              <SettingsRow label="E-posta" hint="Giriş adresi">
                <div className="settings-row-value settings-row-value--plain settings-row-value--wide">
                  {loading ? '…' : user?.email || '—'}
                </div>
              </SettingsRow>
              <SettingsRow label="Rol" hint="Panel erişim seviyesi">
                <div className="settings-row-value settings-row-value--plain">{roleLabel}</div>
              </SettingsRow>
            </div>
          </section>

          <section className="settings-card">
            <header className="settings-card-header">
              <IconTile>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </IconTile>
              <div>
                <h2>İki adımlı doğrulama (2FA)</h2>
                <p>Authenticator uygulaması ile TOTP — VDS Auth üzerinden.</p>
              </div>
            </header>

            {mfaError ? (
              <div className="settings-banner settings-banner--error" role="alert">{mfaError}</div>
            ) : null}
            {mfaOk ? (
              <div className="settings-banner settings-banner--ok" role="status">{mfaOk}</div>
            ) : null}

            <div className="settings-list">
              <SettingsRow label="Durum" hint="Google Authenticator, 1Password, Authy vb.">
                <div className="settings-pill-row settings-pill-row--end">
                  {mfaLoading ? (
                    <span className="settings-pill">…</span>
                  ) : mfaEnabled ? (
                    <span className="settings-pill settings-pill--ok">Açık</span>
                  ) : (
                    <span className="settings-pill settings-pill--warn">Kapalı</span>
                  )}
                </div>
              </SettingsRow>
            </div>

            {!mfaLoading && !mfaEnabled && !enrollDraft ? (
              <button
                type="button"
                className="settings-btn settings-btn--primary"
                onClick={handleStartEnroll}
                disabled={mfaBusy}
              >
                {mfaBusy ? 'Hazırlanıyor…' : '2FA’yı etkinleştir'}
              </button>
            ) : null}

            {enrollDraft ? (
              <form className="settings-form settings-mfa-enroll" onSubmit={handleConfirmEnroll}>
                <p className="settings-mfa-help">
                  QR kodu authenticator uygulamanızla tarayın veya gizli anahtarı elle girin. Ardından 6 haneli kodu yazın.
                </p>
                {enrollDraft.qr?.startsWith('data:') ? (
                  <img className="settings-mfa-qr-img" src={enrollDraft.qr} alt="2FA QR kodu" />
                ) : enrollDraft.qr?.includes('<svg') ? (
                  <div
                    className="settings-mfa-qr"
                    dangerouslySetInnerHTML={{ __html: enrollDraft.qr }}
                  />
                ) : null}
                {enrollDraft.secret ? (
                  <div className="settings-field">
                    <span>Gizli anahtar</span>
                    <code className="settings-mfa-secret">{enrollDraft.secret}</code>
                  </div>
                ) : null}
                <label className="settings-field">
                  <span>Doğrulama kodu</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    disabled={mfaBusy}
                    required
                  />
                </label>
                <div className="settings-mfa-actions">
                  <button
                    type="submit"
                    className="settings-btn settings-btn--primary"
                    disabled={mfaBusy || enrollCode.length !== 6}
                  >
                    {mfaBusy ? 'Doğrulanıyor…' : 'Onayla'}
                  </button>
                  <button
                    type="button"
                    className="settings-btn settings-btn--secondary"
                    onClick={handleCancelEnroll}
                    disabled={mfaBusy}
                  >
                    İptal
                  </button>
                </div>
              </form>
            ) : null}

            {mfaEnabled && !enrollDraft ? (
              showDisable ? (
                <form className="settings-form" onSubmit={handleDisableMfa}>
                  <p className="settings-mfa-help">
                    2FA’yı kapatmak için authenticator kodunu girin.
                  </p>
                  <label className="settings-field">
                    <span>Doğrulama kodu</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      disabled={mfaBusy}
                      required
                    />
                  </label>
                  <div className="settings-mfa-actions">
                    <button
                      type="submit"
                      className="settings-btn settings-btn--danger settings-btn--inline"
                      disabled={mfaBusy || disableCode.length !== 6}
                    >
                      {mfaBusy ? 'Kapatılıyor…' : '2FA’yı kapat'}
                    </button>
                    <button
                      type="button"
                      className="settings-btn settings-btn--secondary"
                      onClick={() => {
                        setShowDisable(false);
                        setDisableCode('');
                        setMfaError('');
                      }}
                      disabled={mfaBusy}
                    >
                      Vazgeç
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="settings-btn settings-btn--secondary"
                  onClick={() => {
                    setShowDisable(true);
                    setMfaOk('');
                    setMfaError('');
                  }}
                  disabled={mfaBusy}
                >
                  2FA’yı kapat
                </button>
              )
            ) : null}
          </section>

          <section className="settings-card">
            <header className="settings-card-header">
              <IconTile>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              </IconTile>
              <div>
                <h2>Görünüm</h2>
                <p>Tema ve dil bu cihazda saklanır.</p>
              </div>
            </header>

            <div className="settings-list">
              <SettingsRow label="Tema" hint="Aydınlık, karanlık veya sistem" stack>
                <div className="settings-segment" role="tablist" aria-label="Tema">
                  {[
                    {
                      id: 'light',
                      label: 'Aydınlık',
                      icon: (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="4" />
                          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                        </svg>
                      ),
                    },
                    {
                      id: 'dark',
                      label: 'Karanlık',
                      icon: (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 14.5A8.5 8.5 0 1110.5 3a7 7 0 0010.5 11.5z" />
                        </svg>
                      ),
                    },
                    {
                      id: 'system',
                      label: 'Sistem',
                      icon: (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="14" rx="2" />
                          <path d="M8 21h8" />
                        </svg>
                      ),
                    },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={theme === opt.id}
                      className={`settings-segment-btn ${theme === opt.id ? 'is-active' : ''}`}
                      onClick={() => setTheme(opt.id)}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </SettingsRow>
              <SettingsRow label="Dil" hint="Tarih biçimi ve arayüz dili" stack>
                <div className="settings-segment settings-segment--compact" role="tablist" aria-label="Dil">
                  {[
                    { id: 'tr', label: 'Türkçe' },
                    { id: 'en', label: 'English' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={lang === opt.id}
                      className={`settings-segment-btn ${lang === opt.id ? 'is-active' : ''}`}
                      onClick={() => setLang(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </SettingsRow>
            </div>
          </section>

          <section className="settings-card settings-card--danger">
            <header className="settings-card-header">
              <IconTile>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <path d="M16 17l5-5-5-5M21 12H9" />
                </svg>
              </IconTile>
              <div>
                <h2>Oturum</h2>
                <p>Yalnızca bu tarayıcıdaki oturumu kapatır.</p>
              </div>
            </header>
            <button type="button" className="settings-btn settings-btn--danger" onClick={handleSignOut}>
              Oturumu kapat
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
